import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../db";
import {
  patients as patientsTable,
  cases as casesTable,
  units as unitsTable,
} from "../../../shared/src/schema";
import { eq } from "drizzle-orm";
import { useCommand, useRpc, useStore } from "../store";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { useStyles, useColors, getTriageColor } from "../ui";
import { useAnon } from "../anon";
import type { PatientDetail } from "../../../shared/src/protocol";
import { MILITARY_RANK_VALUES } from "../../../shared/src/enums";
import { uuidv7 } from "../../../shared/src/ids";
import { UnitAutocomplete } from "../components/UnitAutocomplete";
import { DateTimeInputPopover } from "../components/DateTimePicker";
import { ConfirmModal } from "../confirm-modal";

export function PatientPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();
  const dispatch = useCommand();
  const rpc = useRpc();
  const { online } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const styles = useStyles();
  const colors = useColors();
  const triageColors = getTriageColor(colors);
  const { isAnon, anonName, anonUnit, anonDob } = useAnon();

  const [patient, setPatient] = useState<PatientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [dobOpen, setDobOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ kind: "blocked"; message: string } | { kind: "archive" } | { kind: "confirm" } | null>(null);
  const isAdmin = user?.role === "admin";

  const fetchPatient = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }
    try {
      if (online) {
        const data = await rpc<{ patient: PatientDetail }>({
          type: "getPatient",
          patientId: id,
        });
        setPatient(data.patient);
      } else {
        const rows = await db.select().from(patientsTable).where(eq(patientsTable.id, id));
        const p = rows[0];
        if (!p) { setPatient(null); setLoading(false); return; }
        const [allUnits, cases] = await Promise.all([
          db.select().from(unitsTable),
          db.select().from(casesTable).where(eq(casesTable.patientId, id)),
        ]);
        const uMap = new Map(allUnits.map((u) => [u.id, u]));
        setPatient({
          ...p,
          archived: p.archived ?? 0,
          unitName: p.unitId ? (uMap.get(p.unitId)?.name ?? "") : "",
          cases: cases.map((c) => ({
            id: c.id,
            number: c.number,
            triageCategory: c.triageCategory,
            eventTime: c.eventTime,
            eventLocation: c.eventLocation,
          })),
        });
      }
    } catch {
      setPatient(null);
    } finally {
      setLoading(false);
    }
  }, [rpc, id, online]);

  useEffect(() => {
    fetchPatient();
  }, [fetchPatient]);

  // Optimistic local updates + dispatch command (no re-fetch needed for edits)
  const set = (type: string, payload: Record<string, unknown>) => {
    dispatch(type as any, { uuid: id, ...payload } as any);
  };

  // Helper to update a patient field optimistically + dispatch
  const updateField = (
    field: keyof PatientDetail,
    commandType: string,
    payloadKey: string,
    value: string,
  ) => {
    setPatient((prev) => (prev ? { ...prev, [field]: value || null } : prev));
    set(commandType, { [payloadKey]: value });
  };

  // For text/date inputs: update UI on every keystroke, dispatch only on blur
  const updateFieldLocal = (field: keyof PatientDetail, value: string) => {
    setPatient((prev) => (prev ? { ...prev, [field]: value || null } : prev));
  };
  const commitField = (
    commandType: string,
    payloadKey: string,
    value: string,
  ) => {
    set(commandType, { [payloadKey]: value });
  };

  const createCase = async () => {
    if (!id) return;
    const caseUuid = uuidv7();
    const now = Date.now();

    await dispatch("case.create", { uuid: caseUuid });
    await dispatch("case.setPatient", { uuid: caseUuid, patientUuid: id });
    await dispatch("case.setEventTime", { uuid: caseUuid, timestamp: now });

    const hospitalId = user?.hospitalIds?.[0];
    if (hospitalId) {
      await dispatch("case.setHospital", { uuid: caseUuid, hospitalUuid: hospitalId });
    }
    if (user && (user.role === "medicRole1" || user.role === "medicRole2")) {
      await dispatch("case.setMedicName", { uuid: caseUuid, name: user.name || "" });
    }

    navigate(`/cases/${caseUuid}`);
  };

  const archivePatient = async () => {
    if (!id || !patient) return;
    await Promise.all(patient.cases.map((c) => dispatch("case.archive", { uuid: c.id })));
    await dispatch("patient.archive", { uuid: id });
    setPatient((prev) => prev ? { ...prev, archived: 1 } : prev);
    setDeleteModal(null);
  };

  const requestDelete = async () => {
    if (!id) return;
    try {
      const result = await rpc<{ deletable: boolean; reason?: string }>({ type: "canDelete", entityType: "patient", entityId: id });
      if (result.deletable) {
        setDeleteModal({ kind: "confirm" });
      } else {
        setDeleteModal({ kind: "blocked", message: t("common.deleteBlocked", { reason: result.reason || "" }) });
      }
    } catch { /* ignore */ }
  };

  const confirmDelete = async () => {
    if (!id) return;
    setDeleteModal(null);
    try {
      await rpc({ type: "deleteEntity", entityType: "patient", entityId: id });
      navigate("/patients");
    } catch (e) {
      setDeleteModal({ kind: "blocked", message: (e as any)?.message || "Delete failed" });
    }
  };

  if (loading)
    return (
      <div style={{ padding: "12px", color: colors.textMuted }}>
        {t("app.loading")}
      </div>
    );
  if (!patient)
    return (
      <div style={{ padding: "12px", color: colors.textMuted }}>
        {t("patient.notFound") || "Patient not found"}
      </div>
    );

  return (
    <div>
      <div style={styles.toolbar}>
        <span style={{ fontWeight: 600, color: colors.text }}>
          {anonName(patient.name) || t("patient.new")}
          {patient.archived ? (
            <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem", color: colors.textMuted }}>
              ({t("patient.archived")})
            </span>
          ) : null}
        </span>
        <span style={{ flex: 1 }} />
        <button
          onClick={async () => {
            if (patient.archived) {
              await Promise.all(patient.cases.map((c: { id: string }) => dispatch("case.unarchive", { uuid: c.id })));
              dispatch("patient.unarchive", { uuid: id! });
              setPatient((prev) => prev ? { ...prev, archived: 0 } : prev);
            } else if (patient.cases.length > 0) {
              setDeleteModal({ kind: "archive" });
            } else {
              archivePatient();
            }
          }}
          style={{ ...styles.btn, color: patient.archived ? colors.text : colors.danger }}
        >
          {patient.archived ? t("patient.unarchive") : t("patient.archive")}
        </button>
        {isAdmin && (
          <button onClick={requestDelete} style={{ ...styles.btn, color: colors.danger }}>
            {t("common.delete")}
          </button>
        )}
        <button onClick={createCase} style={styles.btnPrimary}>
          {t("cases.new")}
        </button>
      </div>

      <div style={styles.grid2}>
        <div>
          <div style={styles.label}>{t("patient.name")}</div>
          {isAnon ? (
            <div style={{ ...styles.input, color: colors.textMuted }}>—</div>
          ) : (
            <input
              style={styles.input}
              value={patient.name || ""}
              onChange={(e) => updateFieldLocal("name", e.target.value)}
              onBlur={(e) =>
                commitField("patient.setName", "name", e.target.value)
              }
            />
          )}
        </div>
        <div>
          <div style={styles.label}>{t("patient.dob")}</div>
          <div
            style={{ ...styles.input, cursor: isAnon ? "default" : "pointer", minHeight: "1.5em" }}
            onClick={() => { if (!isAnon) setDobOpen(true); }}
          >
            {isAnon ? (
              <span style={{ color: colors.textMuted }}>—</span>
            ) : (() => {
              const d = anonDob(patient.dateOfBirth);
              if (!d) return <span style={{ color: colors.textMuted }}>дд.мм.рррр</span>;
              const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
              return m ? `${m[3]}.${m[2]}.${m[1]}` : d;
            })()}
          </div>
          {dobOpen && (
            <DateTimeInputPopover
              mode="date"
              dateStr={patient.dateOfBirth}
              label={t("patient.dob")}
              onCommitDate={(date) => {
                setPatient((prev) =>
                  prev ? { ...prev, dateOfBirth: date } : prev,
                );
                set("patient.setDateOfBirth", { date: date || "" });
              }}
              onClose={() => setDobOpen(false)}
              colors={colors}
              t={t}
            />
          )}
        </div>
        <div>
          <div style={styles.label}>{t("patient.sex")}</div>
          <select
            style={styles.select}
            value={patient.sex || ""}
            onChange={(e) =>
              updateField("sex", "patient.setSex", "sex", e.target.value)
            }
          >
            <option value="">—</option>
            <option value="male">{t("sex.male")}</option>
            <option value="female">{t("sex.female")}</option>
            <option value="unknown">{t("sex.unknown")}</option>
          </select>
        </div>
        <div>
          <div style={styles.label}>{t("patient.bloodType")}</div>
          <div style={{ display: "flex", gap: "4px" }}>
            <select
              style={{ ...styles.select, flex: 1 }}
              value={patient.bloodType || ""}
              onChange={(e) =>
                updateField(
                  "bloodType",
                  "patient.setBloodType",
                  "bloodType",
                  e.target.value,
                )
              }
            >
              <option value="">—</option>
              {["A", "B", "AB", "O"].map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
            <select
              style={{ ...styles.select, flex: 1 }}
              value={patient.rhFactor || ""}
              onChange={(e) =>
                updateField(
                  "rhFactor",
                  "patient.setRhFactor",
                  "rhFactor",
                  e.target.value,
                )
              }
            >
              <option value="">—</option>
              <option value="positive">Rh+</option>
              <option value="negative">Rh-</option>
            </select>
          </div>
        </div>
        <div>
          <div style={styles.label}>{t("patient.allergies")}</div>
          <div style={{ display: "flex", gap: "4px" }}>
            <select
              style={{ ...styles.select, flex: "0 0 auto" }}
              value={patient.allergyStatus || ""}
              onChange={(e) =>
                updateField(
                  "allergyStatus",
                  "patient.setAllergyStatus",
                  "status",
                  e.target.value,
                )
              }
            >
              <option value="">—</option>
              <option value="yes">{t("common.yes")}</option>
              <option value="no">{t("common.no")}</option>
              <option value="unknown">{t("sex.unknown")}</option>
            </select>
            {patient.allergyStatus === "yes" && (
              <input
                style={{ ...styles.input, flex: 1 }}
                value={patient.allergyDetails || ""}
                onChange={(e) =>
                  updateFieldLocal("allergyDetails", e.target.value)
                }
                onBlur={(e) =>
                  commitField(
                    "patient.setAllergyDetails",
                    "details",
                    e.target.value,
                  )
                }
              />
            )}
          </div>
        </div>
        <div>
          <div style={styles.label}>{t("patient.rank")}</div>
          <select
            style={styles.select}
            value={patient.militaryRank || ""}
            onChange={(e) =>
              updateField(
                "militaryRank",
                "patient.setMilitaryRank",
                "rank",
                e.target.value,
              )
            }
          >
            <option value="">—</option>
            {MILITARY_RANK_VALUES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <div style={styles.label}>{t("patient.unit")}</div>
          {isAnon ? (
            <div style={{ ...styles.input, color: colors.textMuted }}>
              {anonUnit(patient.unitId, patient.unitName) || "—"}
            </div>
          ) : (
            <UnitAutocomplete
              value={patient.unitName || ""}
              inputStyle={styles.input}
              onSelect={(unit) => {
                setPatient((prev) =>
                  prev ? { ...prev, unitId: unit.id, unitName: unit.name } : prev,
                );
                set("patient.setUnit", { unitUuid: unit.id });
              }}
            />
          )}
        </div>
      </div>

      {/* Medical History */}
      <div style={{ marginTop: "1.5rem", padding: "0 0.75rem 0.75rem" }}>
        <h3 style={{ margin: "0 0 0.5rem", color: colors.text }}>
          {t("patient.history")}
        </h3>
        {patient.cases && patient.cases.length > 0 ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>{t("case.number")}</th>
                <th style={styles.th}>{t("case.triage")}</th>
                <th style={styles.th}>{t("case.eventTime")}</th>
                <th style={styles.th}>{t("case.eventLocation")}</th>
              </tr>
            </thead>
            <tbody>
              {patient.cases.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/cases/${c.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  <td style={styles.td}>{c.number || "—"}</td>
                  <td style={styles.td}>
                    {c.triageCategory ? (
                      <span
                        style={{
                          display: "inline-block",
                          padding: "0.0625rem 0.5rem",
                          borderRadius: "0.25rem",
                          color: "#fff",
                          background:
                            triageColors[c.triageCategory] || colors.textMuted,
                          fontSize: "0.75rem",
                        }}
                      >
                        {c.triageCategory}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td style={styles.td}>
                    {c.eventTime ? new Date(c.eventTime).toLocaleString() : "—"}
                  </td>
                  <td style={styles.td}>{c.eventLocation || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ color: colors.textMuted }}>{t("cases.noResults")}</div>
        )}
      </div>
      {deleteModal?.kind === "archive" && (
        <ConfirmModal
          message={t("common.archiveConfirm", { name: patient.name || t("patient.new") })}
          subMessage={t("patient.archiveWithCases", { count: String(patient.cases.length) })}
          onCancel={() => setDeleteModal(null)}
          onConfirm={archivePatient}
          confirmLabel={t("patient.archive")}
        />
      )}
      {deleteModal?.kind === "blocked" && <ConfirmModal message={deleteModal.message} onCancel={() => setDeleteModal(null)} />}
      {deleteModal?.kind === "confirm" && (
        <ConfirmModal
          message={t("common.deleteConfirm", { name: patient.name || t("patient.new") })}
          onCancel={() => setDeleteModal(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}
